import {useState} from 'react';
import {motion, useReducedMotion} from 'motion/react';
import {asset} from '@/lib/asset';

/**
 * Recap del Navimarket 2025 en YouTube.
 *
 * Fachada, no iframe: hasta que alguien pulsa play sólo se pinta la portada
 * (servida desde /public, no desde YouTube) y no se carga nada de terceros.
 * Al pulsar, se monta el iframe con autoplay — un solo clic, como si el
 * reproductor hubiera estado ahí siempre.
 */
const VIDEO_ID = '1EkyOA6CO9U';
const WATCH_URL = `https://www.youtube.com/watch?v=${VIDEO_ID}`;

export function RecapVideo() {
  const [playing, setPlaying] = useState(false);
  const reduced = useReducedMotion();

  return (
    <figure className="overflow-hidden rounded-3xl border border-white/10 bg-pine-950 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.9)]">
      <div className="relative aspect-video w-full">
        {playing ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${VIDEO_ID}?autoplay=1&rel=0&modestbranding=1`}
            title="Recap del Navimarket 2025"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="absolute inset-0 size-full border-0"
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              setPlaying(true);
            }}
            aria-label="Reproducir el recap del Navimarket 2025"
            className="group absolute inset-0 size-full cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-amber"
          >
            <img
              src={asset('img/recap-2025.webp')}
              alt=""
              className="size-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              loading="lazy"
            />
            {/* Velo verde: la portada es de día y el sitio es de noche. */}
            <span
              aria-hidden
              className="absolute inset-0 bg-gradient-to-t from-pine-950/85 via-pine-950/25 to-pine-950/45"
            />
            <motion.span
              aria-hidden
              initial={false}
              whileHover={reduced ? undefined : {scale: 1.08}}
              transition={{type: 'spring', stiffness: 320, damping: 22}}
              className="absolute left-1/2 top-1/2 grid size-20 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-tangerine shadow-[0_12px_40px_-8px_rgba(222,118,57,0.75)] sm:size-24"
            >
              <svg viewBox="0 0 24 24" className="ml-1 size-9 fill-white sm:size-10">
                <path d="M8 5.5v13a1 1 0 0 0 1.54.84l10-6.5a1 1 0 0 0 0-1.68l-10-6.5A1 1 0 0 0 8 5.5Z" />
              </svg>
            </motion.span>
            <span className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-5 sm:p-6">
              <span className="text-left">
                <span className="overline-label block text-amber">Navimarket · 14 de diciembre de 2025</span>
                <span className="mt-1 block text-lg font-bold text-white sm:text-xl">
                  +500 comercios locales dijeron presente
                </span>
              </span>
              <span className="hidden shrink-0 rounded-full border border-white/20 bg-pine-950/70 px-3 py-1.5 text-xs text-cream/80 sm:block">
                Ver en YouTube
              </span>
            </span>
          </button>
        )}
      </div>
      <figcaption className="sr-only">
        Video recap del Navimarket 2025.{' '}
        <a href={WATCH_URL} target="_blank" rel="noreferrer">
          Verlo en YouTube
        </a>
        .
      </figcaption>
    </figure>
  );
}
